type Child = Node | string | number | null | undefined | false | Child[];
type Props = Record<string, unknown>;

/** A tiny element builder: h("button", { class: "btn", onclick }, "Texte"). */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") el.addEventListener(key.slice(2), value as EventListener);
    else if (key === "class") el.className = String(value);
    else if (key === "html") el.innerHTML = String(value);
    else if (value === true) el.setAttribute(key, "");
    else el.setAttribute(key, String(value));
  }
  append(el, children);
  return el;
}

export function append(el: Element, children: Child[]): void {
  for (const child of children.flat(Infinity as 1) as Child[]) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child instanceof Node ? child : String(child));
  }
}

export function replace(el: Element, ...children: Child[]): void {
  el.replaceChildren();
  append(el, children);
}

/** Polite screen-reader announcement (moves, progress, results). */
export function announce(message: string): void {
  const live = document.getElementById("live");
  if (!live) return;
  live.textContent = "";
  window.setTimeout(() => (live.textContent = message), 30);
}

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });
export const formatDate = (iso: string | null) => (iso ? dateFormat.format(new Date(iso)) : "—");
export const formatBytes = (n: number) => (n < 1024 ? `${n} o` : n < 1048576 ? `${(n / 1024).toFixed(0)} Ko` : `${(n / 1048576).toFixed(1)} Mo`);
export const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

export function notice(kind: "info" | "ok" | "warn" | "bad", title: string, body: Child, ...actions: Child[]): HTMLElement {
  const marks = { info: "i", ok: "✓", warn: "!", bad: "×" };
  return h(
    "div",
    { class: `notice ${kind}`, role: kind === "bad" || kind === "warn" ? "alert" : "status" },
    h("span", { class: "mark", "aria-hidden": "true" }, marks[kind]),
    h("div", null, h("h3", null, title), typeof body === "string" ? h("p", null, body) : body, actions.length ? h("div", { class: "actions" }, actions) : null),
  );
}
